/**
 * Single source of truth for "does this route URL render inside the
 * authenticated wrapper, or as a bare <router-outlet>?"
 *
 * Two places care about this:
 *   1. AppRootComponent — picks which template branch renders.
 *   2. AuthenticatedWrapperComponent.shouldShowWrapper — defensive gate so
 *      the wrapper chrome collapses even if it gets mounted on one of these
 *      routes for any reason (e.g. first tick before NavigationEnd fires).
 *
 * Keeping them in sync by sharing one predicate prevents layout drift.
 */

/**
 * Route URL prefixes that render WITHOUT the authenticated wrapper chrome.
 * Matching is exact-equal or startsWith(prefix + '/').
 */
export const UNAUTHENTICATED_LAYOUT_PREFIXES: readonly string[] = [
    '/auth',
    '/redirector',
    '/session-expired',
    '/access-denied',
    '/contact-admin',
    '/style-showcase',
] as const;

/**
 * Returns true iff the given URL should render inside the authenticated
 * wrapper. Strips query string and hash. Treats '' and '/' as unauthenticated
 * (the root path is the redirector).
 */
export function shouldUseAuthenticatedLayout(url: string): boolean {
    const pathOnly: string = url.split('?')[0].split('#')[0];
    if (pathOnly === '' || pathOnly === '/') return false;
    return !UNAUTHENTICATED_LAYOUT_PREFIXES.some(
        (prefix: string): boolean => pathOnly === prefix || pathOnly.startsWith(prefix + '/')
    );
}
